import { Alert, Box, type SystemStyleObject } from "@hightouch/htui";
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { ErrorBoundary } from "react-error-boundary";
import type { Components } from "react-markdown";

import { DangerousMarkdownWithHtml } from "src/components/llm/markdown/dangerous-markdown-with-html";
import { rehypeReportBlockSafety } from "src/components/markdown-editor/rehype-report-block-safety";

const REHYPE_PLUGINS_AFTER_RAW = [rehypeReportBlockSafety];

// The inherited text styles of the report pages (the root font size and the CSS reset's line height and font features), set so the editor surface can't change them.
const REPORT_DOC_TYPOGRAPHY: SystemStyleObject = {
  color: "text.primary",
  fontSize: "1rem",
  lineHeight: 1.5,
  whiteSpace: "normal",
  overflowWrap: "normal",
  fontVariantLigatures: "normal",
  fontFeatureSettings: '"kern"',
  // A selected block sits inside the editor's DOM selection; without this its text takes the brand selection color instead of showing only the outline.
  userSelect: "none",
  // Kept inline styles could otherwise cover the app: the transform pins fixed elements to the block and the clip cuts off anything drawn outside it, without changing margin collapse.
  transform: "translateX(0)",
  overflow: "clip",
};

const LiveDataPlaceholder = () => (
  <Box
    my={4}
    p={4}
    borderRadius="md"
    bg="base.lightBackground"
    color="text.secondary"
    fontSize="sm"
  >
    Live data
  </Box>
);

// Each of these queries ad data when it mounts, and a folder of report docs mounts every thumbnail at once.
const THUMBNAIL_COMPONENTS: Components = {
  "ad-performance-table": LiveDataPlaceholder,
  "ad-card-grid": LiveDataPlaceholder,
  "campaign-drilldown": LiveDataPlaceholder,
  "ad-set-drilldown": LiveDataPlaceholder,
  "ad-drilldown": LiveDataPlaceholder,
} as Components;

const BLOCK_ERROR = (
  <Alert
    type="error"
    title="Couldn't show this block"
    message="Its markup can't be displayed. Delete the block, or ask the agent to rewrite it."
  />
);

/** A report block's markup as the report pages draw it. */
export const ReportBlockView = ({
  source,
  isThumbnail = false,
}: {
  source: string;
  isThumbnail?: boolean;
}) => (
  <Box sx={REPORT_DOC_TYPOGRAPHY}>
    {/* A doc author's markup can make a component throw; without this one block would take down the editor or the folder view. */}
    <ErrorBoundary fallback={BLOCK_ERROR}>
      <DangerousMarkdownWithHtml
        rehypePluginsAfterRaw={REHYPE_PLUGINS_AFTER_RAW}
        components={isThumbnail ? THUMBNAIL_COMPONENTS : undefined}
      >
        {source}
      </DangerousMarkdownWithHtml>
    </ErrorBoundary>
  </Box>
);

/** A report block in the editor: drawn by the report renderer, selected and deleted as one piece, never edited inside. */
export const ReportBlockNodeView = ({ node }: NodeViewProps) => (
  <NodeViewWrapper contentEditable={false} data-report-block="">
    <ReportBlockView source={node.attrs.source} />
  </NodeViewWrapper>
);
